import { OverlayToaster, Position } from '@blueprintjs/core';
import { IItem, SearchBar } from 'app/components/search-bar/';
import { Throbber } from 'app/components/throbber';
import { AccessibleFacility } from 'app/redux/profile/user/slice';
import { useAppSelector } from 'app/redux/store';
import { convertToSearchableFacility } from 'app/views/components/change-facility-dialog/convertToSearchableFacility';
import { orderAccessibleFacilities } from 'app/views/components/change-facility-dialog/orderAccessibleFacilities';
import { useI18n } from 'core/hooks/useI18n';
import { useCallback, useMemo, useRef } from 'react';
import { ChangeFacilityHotkey } from './ChangeFacilityHotkey';
import { useCanChangeFacility } from './useCanChangeFacility';
import { useChangeFacilityDialogState } from './useChangeFacilityDialogState';
import { useGoToNewFacility } from './useGoToNewFacility';

export const ChangeFacilityDialog = () => {
  const { l10n } = useI18n('app', 'loading');
  const { l10n: onChangeMessage } = useI18n('app/views/components', 'subnav.changeFacility');
  const toaster = useRef<OverlayToaster>(null);
  const {
    facility,
    user: { accessibleFacilities },
  } = useAppSelector(state => state.profile);
  const canChangeFacility = useCanChangeFacility();
  const goToNewFacility = useGoToNewFacility();

  const searchableFacility = useMemo(() => convertToSearchableFacility(facility), [facility]);
  const items = useMemo(() => {
    if (!accessibleFacilities) return undefined;
    return orderAccessibleFacilities(accessibleFacilities).flatMap(facility => {
      const searchableFacility = convertToSearchableFacility(
        facility,
        projectNamesForSourceFacility(accessibleFacilities, facility.id)
      );
      if (!searchableFacility) return [];
      return searchableFacility;
    });
  }, [accessibleFacilities]);
  const { handlers, isOpen } = useChangeFacilityDialogState();
  const itemSelected = useCallback(
    async (item: IItem) => {
      handlers.CLOSE_SEARCH_BAR();
      if (!accessibleFacilities) return;
      const selectedFacility = accessibleFacilities.find(({ id }) => id === item.key);
      if (!selectedFacility) return;
      goToNewFacility(selectedFacility);
      toaster.current?.show({
        message: onChangeMessage('onChangeMessage', { facilityName: selectedFacility.name }),
      });
    },
    [accessibleFacilities, goToNewFacility, handlers, onChangeMessage]
  );

  if (!(items && facility.map)) return <Throbber>{l10n('facilities')}</Throbber>;
  if (!canChangeFacility) return null;

  return (
    <>
      <ChangeFacilityHotkey />
      <SearchBar
        isOpen={isOpen}
        items={items}
        selectedItem={searchableFacility}
        onItemSelect={itemSelected}
        onClose={handlers.CLOSE_SEARCH_BAR}
      />
      <OverlayToaster position={Position.TOP} ref={toaster} />
    </>
  );
};

const projectNamesForSourceFacility = (facilities: AccessibleFacility[], sourceFacilityId?: string) => {
  return facilities.flatMap(facility =>
    facility.isVirtual && facility.isActive && facility.sourceFacilityGuid === sourceFacilityId ? [facility.name] : []
  );
};
